<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount, inject } from 'vue';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMessagingWindow } from 'dashboard/composables/useMessagingWindow';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { resolveKanbanChipPresentation } from 'dashboard/store/modules/conversations/helpers';
import {
  resolveAiAgentMode,
  isAutonomousMode,
  hasActiveAiAgent,
} from 'dashboard/helper/aiAgentMode';
import Avatar from 'next/avatar/Avatar.vue';
import MessagePreview from './MessagePreview.vue';
import ConversationContextMenu from './contextMenu/Index.vue';
import { useShortcutDock } from 'dashboard/components-next/dock/useShortcutDock';
import { usePinnedConversations } from 'dashboard/composables/usePinnedConversations';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';

const props = defineProps({
  activeLabel: { type: String, default: '' },
  chat: { type: Object, default: () => ({}) },
  // SiteUp V2 (mockup puro): o card não renderiza mais nome da caixa nem
  // agente — essa informação vive dentro da conversa. Os props seguem
  // DECLARADOS porque ChatList.vue e o Kanban ainda os passam; removê-los
  // faria virar atributo solto no DOM via $attrs.
  // eslint-disable-next-line vue/no-unused-properties
  hideInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  // eslint-disable-next-line vue/no-unused-properties
  showAssignee: { type: Boolean, default: false },
  conversationType: { type: String, default: '' },
  selected: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  enableContextMenu: { type: Boolean, default: false },
  allowedContextMenuOptions: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'contextMenuToggle',
  'assignAgent',
  'assignLabel',
  'removeLabel',
  'assignTeam',
  'transferInbox',
  'markAsUnread',
  'markAsRead',
  'assignPriority',
  'updateConversationStatus',
  'deleteConversation',
  'selectConversation',
  'deSelectConversation',
]);

const router = useRouter();
const store = useStore();

const hovered = ref(false);
const showContextMenu = ref(false);
const contextMenu = ref({ x: null, y: null });

// Reset UI state when conversation changes at same index (no :key, instance reused on reorder)
// This prevents context menu/hover state from leaking to a different conversation
// Emit contextMenuToggle(false) to sync parent state if menu was open during recycling
const resetState = () => {
  if (showContextMenu.value) {
    emit('contextMenuToggle', false);
  }
  hovered.value = false;
  showContextMenu.value = false;
  contextMenu.value = { x: null, y: null };
};

watch(() => props.chat.id, resetState);

const currentChat = useMapGetter('getSelectedChat');
const activeInbox = useMapGetter('getSelectedInbox');
const accountId = useMapGetter('getCurrentAccountId');

const chatMetadata = computed(() => props.chat.meta || {});

const senderId = computed(() => chatMetadata.value.sender?.id);

const currentContact = computed(() => {
  return senderId.value
    ? store.getters['contacts/getContact'](senderId.value)
    : {};
});

const isActiveChat = computed(() => {
  return currentChat.value.id === props.chat.id;
});

const unreadCount = computed(() => props.chat.unread_count);

const hasUnread = computed(() => unreadCount.value > 0);

// Relógio reativo (15s) pro tempo relativo do card recalcular sozinho.
const nowTs = ref(Math.floor(Date.now() / 1000));
let clockTimer = null;
onMounted(() => {
  clockTimer = setInterval(() => {
    nowTs.value = Math.floor(Date.now() / 1000);
  }, 15000);
});
onBeforeUnmount(() => {
  if (clockTimer) clearInterval(clockTimer);
});

const lastMessageInChat = computed(() => getLastMessage(props.chat));

// SiteUp V2 (tela 03): hora da última atividade no canto do card, em PT-BR
// relativo curto — hoje mostra HH:MM, antes disso "Ontem"/"Nd"/"Nsem".
const lastActivityAgo = computed(() => {
  const ts =
    props.chat?.timestamp ||
    lastMessageInChat.value?.created_at ||
    props.chat?.created_at;
  if (!ts) return '';
  const s = Math.max(0, nowTs.value - Number(ts));
  if (s < 86400) {
    return new Date(ts * 1000).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  if (s < 172800) return 'Ontem';
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  if (s < 2592000) return `${Math.floor(s / 604800)}sem`;
  if (s < 31536000) return `${Math.floor(s / 2592000)}m`;
  return `${Math.floor(s / 31536000)}a`;
});

const voiceCallData = computed(() => ({
  status: props.chat.additional_attributes?.call_status,
  direction: props.chat.additional_attributes?.call_direction,
}));

const inboxId = computed(() => props.chat.inbox_id);

const inbox = computed(() => {
  return inboxId.value ? store.getters['inboxes/getInbox'](inboxId.value) : {};
});

// SiteUp: modo compacto — vem da prop (kanban etc) OU da preferência do usuário
// na barra de filtros (uiSettings.conversations_quick_filters_<accountId>.compact).
const { uiSettings } = useUISettings();
const quickFiltersCompact = computed(() => {
  const scopedAccountId = accountId.value;
  const scoped =
    scopedAccountId &&
    uiSettings.value?.[`conversations_quick_filters_${scopedAccountId}`]
      ?.compact;
  if (scoped != null) return Boolean(scoped);
  return Boolean(uiSettings.value?.conversations_quick_filters?.compact);
});
const isCompact = computed(() => props.compact || quickFiltersCompact.value);
const isAvatarRail = inject(
  'isWorkbenchAvatarRail',
  computed(() => false)
);
// SiteUp: "modo planilha" — vale SÓ na LISTA de conversas. 1 linha por conversa.
const isListSheet = computed(() => !props.compact && quickFiltersCompact.value);

// SiteUp: faixa lateral = JANELA de atendimento (WhatsApp 24h). Verde → âmbar →
// vermelho conforme fecha. Quando não há faixa, a conversa ATIVA ganha a barra
// azul do mockup 03 (as duas vivem na mesma borda; a de janela carrega
// informação, então vence).
const { level: windowLevel, tooltip: windowTooltip } = useMessagingWindow(
  () => props.chat
);
const urgencyStripeClass = computed(() => {
  switch (windowLevel.value) {
    case 'urgent':
    case 'expired':
      return 'bg-n-ruby-9';
    case 'warning':
      return 'bg-n-amber-9';
    case 'ok':
      return 'bg-n-blue-9';
    default:
      return '';
  }
});

// SiteUp: selo do canal no canto do avatar (cor de marca no glifo).
const channelType = computed(() => inbox.value?.channel_type);
const effectiveChannel = computed(() =>
  channelType.value === 'Channel::Api' ? 'Channel::Whatsapp' : channelType.value
);
const channelIcon = computed(() =>
  channelType.value
    ? getInboxIconByType(
        effectiveChannel.value,
        inbox.value?.medium,
        isActiveChat.value ? 'fill' : 'line'
      )
    : null
);
const CHANNEL_COLORS = {
  'Channel::Whatsapp': '#1D9E75',
  'Channel::WhatsappWeb': '#1D9E75',
  'Channel::Instagram': '#C13584',
  'Channel::FacebookPage': '#1877F2',
  'Channel::Telegram': '#229ED9',
  'Channel::WebWidget': '#534AB7',
};
const channelColor = computed(
  () => CHANNEL_COLORS[effectiveChannel.value] || null
);
const channelTooltip = computed(() =>
  inbox.value?.name ? `Caixa: ${inbox.value.name}` : 'Caixa de entrada'
);

const messagePreviewClass = computed(() => {
  return hasUnread.value ? 'font-medium text-n-slate-12' : 'text-n-slate-11';
});

const conversationPath = computed(() => {
  return frontendURL(
    conversationUrl({
      accountId: accountId.value,
      activeInbox: activeInbox.value,
      id: props.chat.id,
      label: props.activeLabel,
      teamId: props.teamId,
      conversationType: props.conversationType,
      foldersId: props.foldersId,
    })
  );
});

// SiteUp V2 — 3ª linha do card (Lucas 2026-08-09, DEPOIS de ver o mockup puro
// rodando): funil›etapa e etiquetas voltaram. O mockup puro tirou tudo, mas na
// operação real o atendente precisa ver em que etapa o lead está e as
// etiquetas sem abrir a conversa. "Criado há" e "sem resposta há" seguem FORA
// (não foram pedidos de volta) — a linha existe só quando há o que mostrar.
const { isCloudFeatureEnabled } = useAccount();
const isKanbanFeatureEnabled = computed(() =>
  isCloudFeatureEnabled('kanban_board')
);

const kanbanItem = computed(() => {
  if (!props.chat.kanban_items || props.chat.kanban_items.length === 0)
    return null;
  return props.chat.kanban_items[0];
});

const funnelFromStore = computed(() => {
  const funnelId = kanbanItem.value?.funnel_id;
  if (funnelId == null) return null;
  return store.getters['funnel/getFunnelById']?.(funnelId) || null;
});

const kanbanChip = computed(() => {
  if (!isKanbanFeatureEnabled.value) return null;
  return resolveKanbanChipPresentation(kanbanItem.value, funnelFromStore.value);
});

const accountLabels = useMapGetter('labels/getLabels');
const conversationActiveLabels = computed(() =>
  accountLabels.value.filter(({ title }) =>
    (props.chat.labels || []).includes(title)
  )
);
// Opção D: etiquetas viram PONTOS na cor delas ao lado do nome (nome completo
// no tooltip) — não ocupam linha nem competem com a prévia. Máximo 3 pontos;
// o tooltip lista todas.
const labelDots = computed(() => conversationActiveLabels.value.slice(0, 3));
const labelsTooltip = computed(() =>
  conversationActiveLabels.value.map(({ title }) => title).join(' · ')
);

// A 3ª linha existe SÓ pro funil›etapa, e só quando há funil — conversa sem
// card no Kanban continua com as 2 linhas do mockup.
const isWorkbenchCompact = computed(
  () => uiSettings.value?.workbench_list_compact === true
);

const showContextRow = computed(
  () =>
    !isListSheet.value && !isWorkbenchCompact.value && Boolean(kanbanChip.value)
);

const aiActiveAriaLabel = 'IA ativa';
const aiActiveTooltip = 'IA ativa — respondendo automaticamente';

// SiteUp: "IA ativa" no card = Captain autônomo, não pausado, conversa pendente.
const aiPaused = computed(
  () => props.chat?.additional_attributes?.ai_agent?.paused === true
);
const aiActive = computed(
  () =>
    hasActiveAiAgent(props.chat) &&
    isAutonomousMode(resolveAiAgentMode(props.chat)) &&
    !aiPaused.value &&
    props.chat?.status === 'pending'
);

// SiteUp V2 (D2): arrastar a conversa pro dock cria um atalho direto pra ela.
// Só publica o tipo próprio; texto/URI ficam de fora de propósito pra não
// virar drop aceito por editor de texto e colar um JSON na mensagem.
const onDragStart = event => {
  const id = props.chat?.id;
  if (!id || !event.dataTransfer) return;
  event.dataTransfer.setData(
    'application/x-siteup-conversation',
    JSON.stringify({
      id,
      label: currentContact.value?.name || '',
      // Foto do contato/grupo: sem ela o atalho vira "ícone de chat qualquer"
      // (feedback do Lucas 2026-08-10) e não dá pra distinguir dois chats.
      thumbnail: currentContact.value?.thumbnail || '',
    })
  );
  event.dataTransfer.effectAllowed = 'copy';
};

const onCardClick = e => {
  const path = conversationPath.value;
  if (!path) return;

  // Handle Ctrl/Cmd + Click for new tab
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    window.open(
      `${window.chatwootConfig.hostURL}${path}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }

  // Skip if already active
  if (isActiveChat.value) return;

  router.push({ path });
};

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, inbox.value.id);
  } else {
    emit('deSelectConversation', props.chat.id, inbox.value.id);
  }
};

const openContextMenu = e => {
  if (!props.enableContextMenu) return;
  e.preventDefault();
  emit('contextMenuToggle', true);
  contextMenu.value.x = e.pageX || e.clientX;
  contextMenu.value.y = e.pageY || e.clientY;
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  emit('contextMenuToggle', false);
  showContextMenu.value = false;
  contextMenu.value.x = null;
  contextMenu.value.y = null;
};

// SiteUp V2: "Fixar na barra" do menu de contexto — mesmo caminho do drop.
const { addConversation: addConversationToDock } = useShortcutDock();
const { t } = useI18n();
const { isPinned, togglePin, clearUnresolvedPins, MAX_PINNED } =
  usePinnedConversations();
const isPinnedToTop = computed(() => isPinned(props.chat?.id));
const onTogglePinTop = () => {
  const result = togglePin(props.chat?.id);
  // Limite atingido: falhar em silêncio faria a pessoa clicar de novo achando
  // que não pegou.
  if (!result.ok && result.reason === 'limit') {
    // Teto cheio pode ser de pins FANTASMA (conversa apagada ou de outra
    // conta): não aparecem, logo não dá pra desafixar pela UI. Tenta limpar
    // antes de culpar a pessoa — se limpou, refixa direto. Achado P1 do
    // review da fase 1.
    const cleaned = clearUnresolvedPins();
    if (cleaned.ok) {
      togglePin(props.chat?.id);
    } else {
      useAlert(
        t('CONVERSATION.CARD_CONTEXT_MENU.PIN_LIMIT', { count: MAX_PINNED })
      );
    }
  }
  closeContextMenu();
};

const pinToDock = () => {
  if (!props.chat?.id) return;
  addConversationToDock({
    id: props.chat.id,
    label: currentContact.value?.name || '',
    thumbnail: currentContact.value?.thumbnail || '',
  });
  closeContextMenu();
};

const onUpdateConversation = (status, snoozedUntil) => {
  closeContextMenu();
  emit('updateConversationStatus', props.chat.id, status, snoozedUntil);
};

const onAssignAgent = agent => {
  emit('assignAgent', agent, [props.chat.id]);
  closeContextMenu();
};

const onAssignLabel = label => {
  emit('assignLabel', [label.title], [props.chat.id]);
};

const onRemoveLabel = label => {
  emit('removeLabel', [label.title], [props.chat.id]);
};

const onAssignTeam = team => {
  emit('assignTeam', team, props.chat.id);
  closeContextMenu();
};

const onTransferInbox = targetInbox => {
  emit('transferInbox', targetInbox, props.chat.id);
  closeContextMenu();
};

const markAsUnread = () => {
  emit('markAsUnread', props.chat.id);
  closeContextMenu();
};

const markAsRead = () => {
  emit('markAsRead', props.chat.id);
  closeContextMenu();
};

const assignPriority = priority => {
  emit('assignPriority', priority, props.chat.id);
  closeContextMenu();
};

const deleteConversation = () => {
  emit('deleteConversation', props.chat.id);
  closeContextMenu();
};
</script>

<template>
  <!-- SiteUp V2 — tela 03 (mockup puro, decisão do Lucas 2026-08-09): o card é
       avatar 44 + nome com hora à direita + prévia de 1 linha + badge. Chip de
       funil, etiquetas, "criado há" e "sem resposta" saíram do card — essa
       informação vive dentro da conversa. -->
  <div
    class="relative flex flex-grow-0 flex-shrink-0 w-auto max-w-full cursor-pointer conversation transition-colors duration-150 hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 group"
    :class="{
      'active bg-n-solid-2': isActiveChat,
      'bg-n-slate-2': selected,
      'items-center justify-center py-2 px-1': isAvatarRail,
      'items-center gap-2.5 py-1 px-3': isListSheet && !isAvatarRail,
      'items-start gap-[11px]': !isListSheet && !isAvatarRail,
      'py-1.5 px-2': isCompact && !isListSheet && !isAvatarRail,
      'py-[11px] px-4': !isCompact && !isListSheet && !isAvatarRail,
    }"
    draggable="true"
    @click="onCardClick"
    @contextmenu="openContextMenu($event)"
    @dragstart="onDragStart"
  >
    <!-- Borda esquerda: janela de atendimento (informação) OU barra azul do
         item ativo (mockup 03). A de janela vence quando as duas existem. -->
    <span
      v-if="urgencyStripeClass"
      v-tooltip.right="windowTooltip"
      class="absolute inset-y-1 ltr:left-0 rtl:right-0 w-1 rounded-full"
      :class="urgencyStripeClass"
      :aria-label="windowTooltip"
    />
    <span
      v-else-if="isActiveChat"
      class="absolute ltr:left-0 rtl:right-0 top-2.5 bottom-2.5 w-[3px] ltr:rounded-r-[3px] rtl:rounded-l-[3px] bg-n-blue-9"
      aria-hidden="true"
    />
    <div
      class="relative flex-shrink-0"
      :class="isListSheet ? 'my-0' : 'mt-0.5'"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <div
        v-if="!hideThumbnail"
        v-tooltip.right="isAvatarRail ? currentContact.name : channelTooltip"
      >
        <Avatar
          :name="currentContact.name"
          :src="currentContact.thumbnail"
          :size="isAvatarRail ? 36 : isCompact ? 28 : 44"
          rounded-full
        >
          <template #overlay="{ size }">
            <label
              v-if="!isAvatarRail && (hovered || selected)"
              class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
              :style="{ width: `${size}px`, height: `${size}px` }"
              @click.stop
            >
              <input
                :value="selected"
                :checked="selected"
                class="!m-0 cursor-pointer"
                type="checkbox"
                @change="onSelectConversation($event.target.checked)"
              />
            </label>
          </template>
        </Avatar>
        <span
          v-if="isAvatarRail && hasUnread"
          class="absolute -top-0.5 z-20 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-n-blue-9 px-1 text-[10px] font-bold text-white ltr:-right-0.5 rtl:-left-0.5"
        >
          {{ unreadCount > 9 ? '9+' : unreadCount }}
        </span>
        <span
          v-if="channelIcon && !isAvatarRail"
          class="absolute z-20 flex items-center justify-center rounded-full bg-n-background ltr:-right-0.5 rtl:-left-0.5 -bottom-0.5 border-2 border-n-background"
          :class="isCompact ? 'size-3' : 'size-[17px]'"
          aria-hidden="true"
        >
          <span
            :class="[channelIcon, isCompact ? 'size-2' : 'size-2.5']"
            :style="{ color: channelColor || '#888780' }"
          />
        </span>
      </div>
    </div>
    <div
      v-if="!isAvatarRail"
      class="flex-1 min-w-0"
      :class="isListSheet ? 'py-0.5' : 'py-0'"
    >
      <div class="flex items-baseline gap-2 min-w-0">
        <h4
          class="conversation--user text-[14.5px] my-0 truncate min-w-0 flex-1 text-n-slate-12"
          :class="hasUnread ? 'font-extrabold' : 'font-bold'"
        >
          {{ currentContact.name }}
          <!-- Indicador discreto "IA ativa" (some quando o humano assume). -->
          <span
            v-if="aiActive"
            v-tooltip.top="aiActiveTooltip"
            class="i-ri-sparkling-line size-3 inline-block align-middle text-n-teal-11 shrink-0"
            :aria-label="aiActiveAriaLabel"
          />
        </h4>
        <!-- Pin 12px na linha, como o mockup 03 (seção FIXADOS). -->
        <span
          v-if="isPinnedToTop"
          data-testid="conversation-pinned-glyph"
          class="i-ri-pushpin-fill size-3 shrink-0 text-n-slate-10"
          aria-hidden="true"
        />
        <!-- SiteUp V2 (opção D): etiquetas como pontos na cor delas; o nome
             completo de todas fica no tooltip. Não ocupam linha. -->
        <span
          v-if="labelDots.length"
          v-tooltip.top="labelsTooltip"
          class="inline-flex items-center gap-[3px] flex-shrink-0"
          :aria-label="labelsTooltip"
        >
          <span
            v-for="label in labelDots"
            :key="label.id"
            class="block rounded-full size-[7px]"
            :style="{ backgroundColor: label.color }"
          />
        </span>
        <time
          v-if="lastActivityAgo"
          class="text-[12.5px] whitespace-nowrap flex-shrink-0 tabular-nums"
          :class="hasUnread ? 'text-n-blue-9 font-semibold' : 'text-n-slate-10'"
        >
          {{ lastActivityAgo }}
        </time>
      </div>
      <div class="flex items-center gap-2 min-w-0 mt-0.5">
        <VoiceCallStatus
          v-if="voiceCallData.status"
          key="voice-status-row"
          :status="voiceCallData.status"
          :direction="voiceCallData.direction"
          :message-preview-class="messagePreviewClass"
          class="flex-1 min-w-0"
        />
        <MessagePreview
          v-else-if="lastMessageInChat"
          key="message-preview"
          :message="lastMessageInChat"
          show-inbound-icon
          class="my-0 leading-5 min-w-0 text-[13.5px] flex-1 h-5 truncate"
          :class="messagePreviewClass"
        />
        <p
          v-else
          key="no-messages"
          class="text-n-slate-11 text-[13.5px] my-0 leading-5 min-w-0 flex-1 h-5 truncate"
        >
          <span
            class="i-ri-information-line size-4 -mt-0.5 align-middle inline-block text-n-slate-10"
          />
          <span class="mx-0.5">
            {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
          </span>
        </p>
        <span
          v-if="hasUnread"
          class="inline-flex items-center justify-center rounded-full text-xs font-bold h-5 min-w-5 px-1.5 py-0 text-center text-white bg-n-blue-9 flex-shrink-0 tabular-nums"
        >
          {{ unreadCount > 9 ? '9+' : unreadCount }}
        </span>
      </div>
      <!-- SiteUp V2 (opção D, Lucas 2026-08-09): 3ª linha só com funil›etapa.
           Só existe quando a conversa tem card no Kanban — sem isso o card
           volta a ter 2 linhas. -->
      <div v-if="showContextRow" class="flex items-center min-w-0 mt-1">
        <span
          class="inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-full bg-n-alpha-2 text-[11.5px] min-w-0"
        >
          <span class="truncate text-n-slate-11">{{
            kanbanChip.funnelName
          }}</span>
          <!-- Separador decorativo via CSS: não é texto traduzível. -->
          <span
            class="text-n-slate-9 flex-shrink-0 before:content-['›']"
            aria-hidden="true"
          />
          <span class="truncate font-semibold text-n-slate-12">{{
            kanbanChip.stageLabel
          }}</span>
        </span>
      </div>
    </div>
    <ContextMenu
      v-if="showContextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :status="chat.status"
        :inbox-id="inbox.id"
        :priority="chat.priority"
        :chat-id="chat.id"
        :is-pinned-to-top="isPinnedToTop"
        :has-unread-messages="hasUnread"
        :conversation-labels="chat.labels"
        :conversation-url="conversationPath"
        :allowed-options="allowedContextMenuOptions"
        @update-conversation="onUpdateConversation"
        @assign-agent="onAssignAgent"
        @assign-label="onAssignLabel"
        @remove-label="onRemoveLabel"
        @assign-team="onAssignTeam"
        @transfer-inbox="onTransferInbox"
        @toggle-pin-top="onTogglePinTop"
        @pin-to-dock="pinToDock"
        @mark-as-unread="markAsUnread"
        @mark-as-read="markAsRead"
        @assign-priority="assignPriority"
        @delete-conversation="deleteConversation"
        @close="closeContextMenu"
      />
    </ContextMenu>
  </div>
</template>
